import { ComponentProps, FC, PropsWithChildren, Suspense, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { DataSourceDefinition, useDataSource } from '@factorialco/f0-react'
import {
  BreadcrumbSelectItemType,
  BreadcrumbsProps,
  Page,
  PageHeader,
  ResourceHeader,
  Spinner,
  Tabs,
} from '@factorialco/f0-react/dist/experimental'
import { Settings } from '@factorialco/f0-react/icons/app'
import { AvailableModules } from 'generated/featureDefinitions'

import {
  GraphqlDataSourceDefinition,
  useGraphqlDataSource,
} from 'modules/core/hooks/useGraphqlDataSource'
import i18n from 'modules/core/lib/i18n'
import { getCurrentModule } from 'modules/core/lib/navigation/routes'
import type { ModuleMeta, NavigationElement } from 'modules/core/lib/navigation/types'

import { useLayoutContext } from 'modules/layout/hooks/useFullscreen'
import { useProductUpdatesHeader } from 'modules/product_updates/utils/useProductUpdatesHeader'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

import { getBreadcrumbs } from './getBreadcrumbs'
import { getSiblingUrl } from './getSiblingUrl'
import { getTabs } from './getTabs'
import { useFavorites } from './useFavorites'
import { usePageStatus } from './usePageStatus'
import { useHandles } from './useResolvedHandles'

type CrossSellingModalState = {
  isOpen: boolean
  moduleRequested: AvailableModules | undefined
}

const getActiveTabs = (
  tabs: ReturnType<typeof getTabs>,
  currentPath: string
): NavigationElement[] =>
  tabs.flatMap((tabGroup) =>
    [...tabGroup].filter((tab) => tab.isVisible && !tab.index && currentPath.startsWith(tab.href))
  ) || []

export const FactorialOnePage = ({
  children,
  onActionClick,
}: PropsWithChildren<{
  onActionClick?: (key: string) => void
}>) => {
  const { resolvedHandles, handles, resolving } = useHandles()
  const {
    breadcrumbs,
    lastResourceIndex,
    lastResource: currentResource,
    moduleIndex,
  } = getBreadcrumbs(handles)
  const currentModule = getCurrentModule(handles, moduleIndex)
  const tabs = getTabs(resolvedHandles, lastResourceIndex)
  const [crossSellingModal, setCrossSellingModal] = useState<CrossSellingModalState>({
    isOpen: false,
    moduleRequested: undefined,
  })

  const buildProductUpdatesSection = useProductUpdatesHeader(
    currentModule?.id || '',
    setCrossSellingModal
  )

  const { pageStatus } = usePageStatus()
  const { isEmbedded } = useLayoutContext()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const activeTabs = useMemo(() => getActiveTabs(tabs, pathname), [tabs, pathname])
  const favorites = useFavorites(currentResource, currentModule?.id)

  /**
   * Construct the source for the breadcrumb select definition
   */
  const breadcrumbSelectSource = useMemo(() => {
    const isBreadcrumbSelect = (
      breadcrumb: BreadcrumbsProps['breadcrumbs'][number]
    ): breadcrumb is BreadcrumbSelectItemType => {
      return 'type' in breadcrumb && breadcrumb.type === 'select'
    }
    const breadcrumb = breadcrumbs.find((breadcrumbItem) => isBreadcrumbSelect(breadcrumbItem))
    return breadcrumb ? breadcrumb.source : undefined
  }, [breadcrumbs])

  const isGraphqlSource = (
    source: Record<string, unknown> | undefined
  ): source is GraphqlDataSourceDefinition => {
    return source !== undefined && 'document' in source && source.document !== undefined
  }

  const isPlainSource = (
    source: Record<string, unknown> | undefined
  ): source is DataSourceDefinition => {
    return source !== undefined && 'dataAdapter' in source && source.dataAdapter !== undefined
  }

  // Depending on the source type, we use the appropriate source
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const sourceGraphql = useGraphqlDataSource(
    (isGraphqlSource(breadcrumbSelectSource) ? breadcrumbSelectSource : undefined) ??
      ({} as GraphqlDataSourceDefinition),
    [breadcrumbSelectSource]
  )
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const sourceDataSource = useDataSource(
    (isPlainSource(breadcrumbSelectSource) ? breadcrumbSelectSource : undefined) ??
      ({} as DataSourceDefinition),
    [breadcrumbSelectSource]
  )

  const source = useMemo(() => {
    return breadcrumbSelectSource && 'document' in breadcrumbSelectSource
      ? sourceGraphql
      : sourceDataSource
  }, [breadcrumbSelectSource, sourceGraphql, sourceDataSource])

  const breadcrumbsWithNavigation = breadcrumbs.map((breadcrumb) => {
    if ('type' in breadcrumb && breadcrumb.type === 'select') {
      if (breadcrumb.onChange || currentResource?.meta?.siblingsNavigation?.select?.url) {
        return {
          ...breadcrumb,
          source: breadcrumb.source ? source : undefined,
          onChange: (value: string) => {
            if (breadcrumb.onChange) {
              breadcrumb.onChange(value)
              return
            }
            currentResource?.meta?.siblingsNavigation?.select?.url &&
              navigate(
                getSiblingUrl(
                  activeTabs[activeTabs.length - 1]?.href,
                  currentResource.meta.siblingsNavigation.select.url(value)
                )
              )
          },
        }
      } else {
        return {
          ...breadcrumb,
          source: breadcrumb.source ? source : undefined,
          type: undefined,
        }
      }
    }
    return breadcrumb
  })

  const noHeaderModules = ['employee_onboarding', 'home']

  const showHeader =
    currentModule && (!noHeaderModules.includes(currentModule.id) || breadcrumbs.length > 0)

  return (
    <Page
      header={
        <>
          {showHeader && (
            <PageHeader
              module={{
                name: currentModule.name,
                href: currentModule.path,
                id: currentModule.id,
              }}
              breadcrumbs={breadcrumbsWithNavigation}
              actions={
                currentModule.settings ? [buildSettingsAction(currentModule.settings)] : undefined
              }
              statusTag={pageStatus}
              embedded={isEmbedded}
              favorites={!resolving ? favorites : undefined}
              navigation={
                currentResource?.meta?.siblingsNavigation
                  ? {
                      next: currentResource.meta.siblingsNavigation.next && {
                        ...currentResource.meta.siblingsNavigation.next,
                        url: getSiblingUrl(
                          activeTabs[activeTabs.length - 1]?.href,
                          currentResource.meta.siblingsNavigation.next.url
                        ),
                      },
                      previous: currentResource.meta.siblingsNavigation.previous && {
                        ...currentResource.meta.siblingsNavigation.previous,
                        url: getSiblingUrl(
                          activeTabs[activeTabs.length - 1]?.href,
                          currentResource.meta.siblingsNavigation.previous.url
                        ),
                      },
                      counter: currentResource.meta.siblingsNavigation.counter,
                    }
                  : undefined
              }
              productUpdates={buildProductUpdatesSection}
            />
          )}
          {currentResource &&
            (currentResource.meta?.headerComponent === 'auto' ? (
              <ResourceHeader
                title={currentResource.meta.title}
                description={currentResource.meta.description ?? undefined}
              />
            ) : currentResource.meta?.headerComponentProps ? (
              <ResourceHeader
                {...currentResource.meta.headerComponentProps}
                primaryAction={
                  currentResource.meta.headerComponentProps.primaryAction && onActionClick
                    ? {
                        ...currentResource.meta.headerComponentProps.primaryAction,
                        onClick: () =>
                          onActionClick(
                            currentResource.meta?.headerComponentProps?.primaryAction?.key || ''
                          ),
                      }
                    : undefined
                }
                secondaryActions={
                  currentResource.meta.headerComponentProps.secondaryActions && onActionClick
                    ? currentResource.meta?.headerComponentProps.secondaryActions.map((action) => ({
                        ...action,
                        onClick: () => onActionClick(action.key),
                      }))
                    : undefined
                }
                otherActions={
                  currentResource.meta.headerComponentProps.otherActions && onActionClick
                    ? currentResource.meta.headerComponentProps.otherActions.map((action) => ({
                        ...action,
                        onClick: () => onActionClick(action.key),
                      }))
                    : undefined
                }
              />
            ) : (
              currentResource.meta?.headerComponent
            ))}
          {tabs.map((tabGroup, index) => (
            <Tabs
              key={index}
              tabs={tabGroup.map((tab) => ({
                label: tab.title,
                href: tab.href,
                index: tab.index,
                variant: tab.variant,
              }))}
              secondary={index > 0}
              embedded={isEmbedded}
            />
          ))}
        </>
      }
      embedded={isEmbedded}
    >
      {resolving ? (
        <Loading key="loading" />
      ) : (
        <Suspense fallback={<Loading key="loading" />}>{children}</Suspense>
      )}
      {crossSellingModal.isOpen && crossSellingModal.moduleRequested && (
        <UpsellingKit.ProductModal
          isOpen={crossSellingModal.isOpen}
          onClose={() => setCrossSellingModal({ isOpen: false, moduleRequested: undefined })}
          requestSource="product-updates-dropdown"
          moduleRequested={crossSellingModal.moduleRequested}
          requestReason={ValidReasons.MissingAddon}
          pageSource={pathname}
        />
      )}
    </Page>
  )
}

function buildSettingsAction(
  settings: NonNullable<ModuleMeta['settings']>
): Required<ComponentProps<typeof PageHeader>>['actions'][number] {
  const baseData = {
    label: i18n.t('module.settings'),
    icon: Settings,
  }

  if (typeof settings === 'object' && 'items' in settings) {
    return {
      ...baseData,
      actions: settings.items.map((item) => ({
        label: item.label,
        href: item.href,
      })),
    }
  }

  return {
    ...baseData,
    href: settings,
  }
}

const Loading: FC = () => (
  <Box justifyContent="center" alignItems="center">
    <Spinner key="loading" />
  </Box>
)
